import { useCallback } from 'react';
import type { AgentExecutionOrderByFieldType } from '@terra/shared-contracts-agent-execution';
import {
  AgentExecutionStatusSchema,
  ExecutionTypeSchema,
  type AgentExecutionStatusType,
  type ExecutionTypeType,
} from '@terra/shared-schemas-ai';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { createTabScopedStore } from './createTabScopedStore';

export type AgentExecutionListSort = {
  colId: AgentExecutionOrderByFieldType;
  sort: 'asc' | 'desc';
} | null;

export type AgentExecutionsTabFilterState = {
  searchTerm: string;
  statusFilter: AgentExecutionStatusType[];
  executionTypeFilter: ExecutionTypeType | null;
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto;
  sort: AgentExecutionListSort;
};

const useAgentExecutionsTabScopedFilterStoreBase =
  createTabScopedStore<AgentExecutionsTabFilterState>({
    searchTerm: '',
    statusFilter: [],
    executionTypeFilter: null,
    createdSince: null,
    lifecycleState: DEFAULT_LIFECYCLE_STATE_FILTER,
    sort: null,
  });

export const isAgentExecutionStatus = (
  status: unknown,
): status is AgentExecutionStatusType =>
  AgentExecutionStatusSchema.safeParse(status).success;

export const isAgentExecutionType = (
  executionType: unknown,
): executionType is ExecutionTypeType =>
  ExecutionTypeSchema.safeParse(executionType).success;

const normalizeStatusFilter = (value: unknown): AgentExecutionStatusType[] =>
  Array.isArray(value) ? value.filter(isAgentExecutionStatus) : [];

const normalizeExecutionTypeFilter = (
  value: unknown,
): ExecutionTypeType | null => (isAgentExecutionType(value) ? value : null);

export function useTabScopedAgentExecutionsFilters() {
  const { filters, update } = useAgentExecutionsTabScopedFilterStoreBase();
  const statusFilter = normalizeStatusFilter(filters.statusFilter);
  const executionTypeFilter = normalizeExecutionTypeFilter(
    filters.executionTypeFilter,
  );

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setStatusFilter = useCallback(
    (next: AgentExecutionStatusType[]) =>
      update({ statusFilter: normalizeStatusFilter(next) }),
    [update],
  );
  const setExecutionTypeFilter = useCallback(
    (next: ExecutionTypeType | null) =>
      update({ executionTypeFilter: normalizeExecutionTypeFilter(next) }),
    [update],
  );
  const setCreatedSince = useCallback(
    (createdSince: Date | null) => update({ createdSince }),
    [update],
  );
  const setLifecycleState = useCallback(
    (lifecycleState: LifecycleStateFilterDto) => update({ lifecycleState }),
    [update],
  );
  const setSort = useCallback(
    (sort: AgentExecutionListSort) => update({ sort }),
    [update],
  );

  return {
    ...filters,
    statusFilter,
    executionTypeFilter,
    setSearchTerm,
    setStatusFilter,
    setExecutionTypeFilter,
    setCreatedSince,
    setLifecycleState,
    setSort,
  };
}
